import ColumnSelector from './ColumnSelector';
import { escapeHtml } from 'features/shared/utility/escapeHtml';
import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import GetAppIcon from '@mui/icons-material/GetApp';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import { TextField, InputAdornment, Popover } from '@mui/material';
import { RMSButton, RMSIconButton } from '../../../buttons/index';
import htmlForPrinting from 'utils/htmlForPrinting';
import { getDataGridToolbarProps } from '../../../../utility/dataGridToolbarHelper';
import KEYBOARD_KEYS from '../../../../constants/keyboardKeys';
import { DOUBLE_QUOTE_REGEX } from '../../../../constants/regex';

const CustomToolbar = ({
  toolbar,

  searchInput,
  setSearchInput,
  onSearch,

  // Search
  search = {
    placeholder: 'Search...',
  },
  layoutSync,
  columnOrdering,
  columnVisibility,
  exportActions = [],
}) => {
  const {
    showSearch,
    showColumnSelector,
    showFilter,
    showDensity,
    showExport,
    showPrint,
    printTitle,
  } = useMemo(() => getDataGridToolbarProps(toolbar), [toolbar]);
  const [anchorEl, setAnchorEl] = useState(null);
  const columnsOpen = Boolean(anchorEl);
  const apiRef = useGridApiContext();

  const printIframeRef = useRef(null);

  const getPrintableColumns = useCallback(
    () =>
      apiRef.current
        .getVisibleColumns()
        .filter((col) => col.field !== 'actions' && col.field !== '__check__'),
    [apiRef]
  );

  const getPrintableCellValue = useCallback(
    (column, row) => {
      const rawValue = column.valueGetter
        ? column.valueGetter({ id: row.id, field: column.field, row, api: apiRef.current })
        : row[column.field];

      const formatted = column.valueFormatter
        ? column.valueFormatter({
            id: row.id,
            field: column.field,
            value: rawValue,
            api: apiRef.current,
          })
        : rawValue;

      if (column.type === 'boolean' && typeof formatted === 'boolean') {
        return formatted ? 'YES' : 'NO';
      }

      return formatted ?? '';
    },
    [apiRef]
  );

  const handleCsvExport = useCallback(() => {
    const columns = getPrintableColumns();
    const rows = Array.from(apiRef.current.getVisibleRowModels().values());

    const headerRow = columns
      .map((col) => `"${(col.headerName || col.field).replace(DOUBLE_QUOTE_REGEX, '""')}"`)
      .join(',');

    const dataRows = rows.map((row) => {
      return columns
        .map((col) => {
          const value = getPrintableCellValue(col, row);
          const str = value === null || value === undefined ? '' : String(value);
          return `"${str.replace(DOUBLE_QUOTE_REGEX, '""')}"`;
        })
        .join(',');
    });

    const csvContent = [headerRow, ...dataRows].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${printTitle || 'export'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [apiRef, getPrintableColumns, getPrintableCellValue, printTitle]);

  const handlePrint = useCallback(() => {
    const columns = getPrintableColumns();
    const rows = Array.from(apiRef.current.getVisibleRowModels().values());

    const tableHtml = `
      <h4>${escapeHtml(printTitle)}</h4>
      <table>
        <thead>
          <tr>${columns
            .map((col) => `<th>${escapeHtml(col.headerName || col.field)}</th>`)
            .join('')}</tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row) =>
                `<tr>${columns
                  .map((col) => `<td>${escapeHtml(getPrintableCellValue(col, row))}</td>`)
                  .join('')}</tr>`
            )
            .join('')}
        </tbody>
      </table>
    `;

    const html = htmlForPrinting(tableHtml, printTitle);
    const win = printIframeRef.current.contentWindow;

    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  }, [apiRef, getPrintableColumns, getPrintableCellValue, printTitle]);

  const handleSearch = useCallback(() => onSearch(searchInput), [onSearch, searchInput]);

  const handleSearchChange = useCallback(
    (event) => {
      const value = event.target.value;
      setSearchInput(value);
      if (!value.trim()) onSearch('');
    },
    [setSearchInput, onSearch]
  );
  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key === KEYBOARD_KEYS.ENTER) handleSearch();
    },
    [handleSearch]
  );

  const handleSearchMouseDown = useCallback((event) => event.preventDefault(), []);

  const handleOpenColumns = useCallback((event) => setAnchorEl(event.currentTarget), []);

  const handleCloseColumns = useCallback(() => setAnchorEl(null), []);

  const handlePrintClick = useCallback(() => {
    if (exportActions.length > 0) {
      const visibleRows = Array.from(apiRef.current.getVisibleRowModels().values());
      const action = exportActions[0];
      const rows = action.maxRows ? visibleRows.slice(0, action.maxRows) : visibleRows;
      action.onClick?.(rows, visibleRows.length);
    } else {
      handlePrint();
    }
  }, [apiRef, exportActions, handlePrint]);

  const searchInputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment position="end">
          <RMSIconButton
            label="Search"
            icon={<SearchIcon className="search-icon" />}
            size="small"
            onMouseDown={handleSearchMouseDown}
            onClick={handleSearch}
          />
        </InputAdornment>
      ),
    }),
    [handleSearchMouseDown, handleSearch]
  );

  return (
    <GridToolbarContainer className="toolbar-container">
      <div className="toolbar-container__left">
        {showSearch && (
          <TextField
            size="small"
            label={search.placeholder}
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearch}
            className="toolbar-container__search"
            InputProps={searchInputProps}
          />
        )}
      </div>

      <div className="toolbar-container__right">
        {showColumnSelector && (
          <>
            <RMSButton
              size="small"
              startIcon={<ViewColumnIcon fontSize="small" />}
              onClick={handleOpenColumns}
              label="COLUMNS"
              variant="text"></RMSButton>

            <Popover
              open={columnsOpen}
              anchorEl={anchorEl}
              onClose={handleCloseColumns}
              // Keep focus inside a containing dialog and avoid competing focus traps.
              disablePortal
              disableEnforceFocus
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
              <ColumnSelector columnOrdering={columnOrdering} columnVisibility={columnVisibility} />
            </Popover>
          </>
        )}
        {showFilter && <GridToolbarFilterButton />}
        {showDensity && <GridToolbarDensitySelector />}
        {showExport && (
          <RMSButton
            size="small"
            startIcon={<GetAppIcon fontSize="small" />}
            onClick={handleCsvExport}
            label="EXPORT"
            variant="text"
          />
        )}
        {showPrint && (
          <RMSButton
            size="small"
            startIcon={<LocalPrintshopRoundedIcon fontSize="small" />}
            onClick={handlePrintClick}
            label="PRINT"
            variant="text"
          />
        )}
        {layoutSync?.enabled && (
          <>
            <RMSButton
              size="small"
              prefixIcon={<SaveIcon fontSize="small" />}
              disabled={layoutSync.saving || !layoutSync.isDirty}
              onClick={layoutSync.saveLayout}
              label="Save Layout"
            />
            <RMSButton
              size="small"
              prefixIcon={<RestartAltIcon fontSize="small" />}
              disabled={layoutSync.saving}
              onClick={layoutSync.resetLayout}
              label="Reset Layout"
            />
          </>
        )}
      </div>

      <iframe
        ref={printIframeRef}
        title="common-data-grid-print-frame"
        className="common-data-grid-print-iframe"
      />
    </GridToolbarContainer>
  );
};

export default React.memo(CustomToolbar);
